// AI breakdown tab — primary analytical surface. One row per BU; columns
// split Won $ across Selling AI / Using AI / Non-AI per the BODQ22026
// rep-tag conventions.
//
// SGNL-134 — walked back the Rep/LLM/Disagree subview toggle that
// SGNL-127 introduced. Surfacing the LLM-vs-rep aggregate gap on an
// exec-visible public surface erodes trust in the rep totals being
// cited (~$75M rep selling-AI vs a potentially much smaller LLM
// aggregate). The reconciling view moved to /admin/ai-audit, which is
// admin-only. This tab is back to rep-only — the same numbers execs
// have always seen. Deal Explorer keeps its LLM columns + filters
// (per-row, less politically charged); the public aggregate doesn't.
//
// Cell-level drill-through (SGNL-61):
//   Every $ column is a clickable link. Clicking applies the appropriate
//   filter combo + switches to Deal Explorer.
//
// Sortable columns (SGNL-63):
//   Click any column header to sort asc/desc. Cycle: same column flips dir,
//   different column starts at desc. NULLs always last.

import { setActiveTab, setFilters } from "../state";
import type { AIClass, Filters, OpportunityRow, StageState } from "../types";
import { computeAIBreakdown, type AIBreakdownRow } from "../lib/aggregations";
import { esc, fmtMoney } from "../lib/format";
import { bindSortHeaders, makeSortable, type SortColumn, type SortState } from "../lib/sortable";
import { drawSortableStacked } from "./charts";

const fmtPct = (v: number | null): string => (v === null ? "—" : `${(v * 100).toFixed(0)}%`);

const COLUMNS: SortColumn<AIBreakdownRow>[] = [
  { key: "bu", label: "BU" },
  { key: "deals", label: "Deals", numeric: true },
  { key: "customers", label: "Customers", numeric: true },
  { key: "selling_ai_won", label: "Selling AI $", numeric: true },
  { key: "using_ai_won", label: "Using AI $", numeric: true },
  { key: "non_ai_won", label: "Non-AI $", numeric: true },
  { key: "won", label: "Won $", numeric: true },
  { key: "lost", label: "Lost $", numeric: true },
  { key: "open", label: "Open $", numeric: true },
  { key: "win_rate", label: "Win rate", numeric: true },
];

// Per-tab sort state. Module-level so it persists across re-renders triggered
// by filter changes; reset to default on first load.
let sortState: SortState<AIBreakdownRow> = { field: "won", dir: "desc" };

function drillCell(
  value: number,
  bu: string | null,
  opts: { aiClass?: AIClass; state?: StageState; stage?: string } = {},
): string {
  const dataAttrs: string[] = [];
  if (bu) dataAttrs.push(`data-bu="${esc(bu)}"`);
  if (opts.aiClass) dataAttrs.push(`data-ai-class="${esc(opts.aiClass)}"`);
  if (opts.state) dataAttrs.push(`data-state="${esc(opts.state)}"`);
  if (opts.stage) dataAttrs.push(`data-stage="${esc(opts.stage)}"`);
  return `<a href="#deals" class="drill-cell" ${dataAttrs.join(" ")}>${fmtMoney(value)}</a>`;
}

export function renderAIBreakdownTab(host: HTMLElement, rows: OpportunityRow[]): void {
  const agg = computeAIBreakdown(rows);
  const { sorted, headHtml } = makeSortable(agg, COLUMNS, sortState);

  host.innerHTML = `
    <div class="tab-content" data-testid="ai-breakdown-tab">
      <div class="tab-header">
        <h2>AI breakdown <span class="row-count">${agg.length} BU${agg.length === 1 ? "" : "s"}</span></h2>
      </div>
      <div class="table-wrap">
        <table class="data-table sortable">
          ${headHtml}
          <tbody>
            ${sorted
              .map(
                (a) => `
              <tr>
                <td><a href="#deals" class="drill-cell" data-bu="${esc(a.bu)}">${esc(a.bu) || "(unset)"}</a></td>
                <td class="num">${a.deals.toLocaleString()}</td>
                <td class="num">${a.customers.toLocaleString()}</td>
                <td class="num">${drillCell(a.selling_ai_won, a.bu, { aiClass: "selling", state: "won" })}</td>
                <td class="num">${drillCell(a.using_ai_won, a.bu, { aiClass: "using", state: "won" })}</td>
                <td class="num">${drillCell(a.non_ai_won, a.bu, { aiClass: "non", state: "won" })}</td>
                <td class="num">${drillCell(a.won, a.bu, { state: "won" })}</td>
                <td class="num">${drillCell(a.lost, a.bu, { state: "lost", stage: "Closed Lost" })}</td>
                <td class="num">${drillCell(a.open, a.bu, { state: "open" })}</td>
                <td class="num">${fmtPct(a.win_rate)}</td>
              </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      </div>
      <div class="chart-row">
        <div class="chart-wrap"><canvas id="chart-ai-won"></canvas></div>
        <div class="chart-wrap"><canvas id="chart-ai-stage"></canvas></div>
      </div>
      <p class="table-footnote" data-testid="ai-breakdown-footnote">
        Classification is rule-based on rep tags (ai_boolean / ai_enablement).
        Click any $ to drill into the matching opps.
        Admin audit (rep vs LLM reconciling view) at /admin/ai-audit.
      </p>
    </div>
  `;

  // SGNL-109 — vertical bars (one bar per BU, stacked) match the prototype
  // and read better for ranking-at-a-glance than the legacy horizontal
  // BODQ22026 layout. Other tabs (customers, lost) keep horizontal.
  drawSortableStacked<AIBreakdownRow>(
    "chart-ai-won",
    agg,
    "bu",
    [
      { label: "Selling AI (Solutions)", key: "selling_ai_won", color: "#0e6f6e" },
      { label: "Using AI (Delivery)", key: "using_ai_won", color: "#d200b5" },
      { label: "Non-AI", key: "non_ai_won", color: "#94a3b8" },
    ],
    "Won $ by BU — sorted by visible total (click legend to toggle)",
    { indexAxis: "x" },
  );

  drawSortableStacked<AIBreakdownRow>(
    "chart-ai-stage",
    agg,
    "bu",
    [
      { label: "Won $", key: "won", color: "#34b4ff" },
      { label: "Open $", key: "open", color: "#853eff" },
      { label: "Lost $", key: "lost", color: "#ff4759" },
    ],
    "BU stage mix — sorted by visible total",
    { indexAxis: "x" },
  );

  // Drill-through click handler.
  for (const a of host.querySelectorAll<HTMLAnchorElement>("a.drill-cell")) {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const patch: Partial<Filters> = {};
      const bu = a.dataset.bu;
      if (bu) patch.bu = bu;
      if (a.dataset.aiClass) patch.aiClass = a.dataset.aiClass as AIClass;
      if (a.dataset.state) patch.state = a.dataset.state as StageState;
      // SGNL-249: stage filter is multi-select; drill-through carries
      // one stage so substitute exactly that stage as the list.
      if (a.dataset.stage) patch.stages = [a.dataset.stage];
      setFilters(patch);
      setActiveTab("deals");
    });
  }

  // Sortable headers.
  bindSortHeaders(host, COLUMNS, sortState, (next) => {
    sortState = next;
    renderAIBreakdownTab(host, rows);
  });
}
